import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateSocialGraph } from "Api/Mutations/Social/invalidateSocialGraph";
import { handleAvailabilityKeys } from "Api/Queries/Social/useHandleAvailabilityQuery";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import {
  type ApiProblemDetails,
  type FetchResponse,
  putSocialHandle,
  type SetHandleCommandResult,
  type SetHandleRequest,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: SetHandleCommandResult) => void;
  // The thrown error is the raw FetchResponse wrapper — problem details live in error.data.
  onError?: (error: FetchResponse<ApiProblemDetails, 409 | 403> | null) => void;
};

/**
 * Sets or changes the active persona's handle; changing releases the old handle immediately.
 * The value is normalized to lowercase and the stored value is returned. The first handle must
 * carry acceptSocialTerms — consent to the social-features terms (ZS-1749).
 */
export const useSetHandleMutation = ({ onSuccess, onError }: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (request: SetHandleRequest) => putSocialHandle(request),
    ),
    onSuccess: (result: SetHandleCommandResult) => {
      queryClient.invalidateQueries({ queryKey: handleAvailabilityKeys.all });
      // The handle is served through user-info — React Query consumers (handle card,
      // profile actions, banner) must see the new value immediately.
      queryClient.invalidateQueries({ queryKey: userInfoKeys.all });
      // The handle is also part of every public profile and search result already cached
      // in this session (own profile preview, a search for oneself), so the graph families
      // are reconciled the same way the follow/block mutations do.
      invalidateSocialGraph(queryClient);
      onSuccess?.(result);
    },
    onError: (error: FetchResponse<ApiProblemDetails, 409 | 403> | null) => {
      onError?.(error);
    },
  });
};
